// Set hearts a signed-out visitor made on this device. localStorage, not
// sessionStorage: a Reddit reader is in an in-app browser they will close, and
// the picks have to survive that to be worth migrating on sign-in.
import { log } from './logger'
import type { InterestStatus } from '../types'

export type LocalPicks = ReadonlyMap<string, InterestStatus>
type Stored = { v: 1; events: Record<string, Record<string, InterestStatus>> }

const STORAGE_KEY = 'beacon_guest_set_picks'
export const GUEST_PICKS_FOOTER_THRESHOLD = 3
export const EMPTY_LOCAL_PICKS: LocalPicks = new Map()

const subscribers = new Set<() => void>()
let stored: Stored | null = null
const eventCache = new Map<string, LocalPicks>()
let totalCache: number | null = null

function read(): Stored {
  if (stored) return stored
  stored = { v: 1, events: {} }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? (JSON.parse(raw) as Partial<Stored>) : null
    if (parsed?.v === 1 && parsed.events && typeof parsed.events === 'object') {
      stored = { v: 1, events: parsed.events }
    }
  } catch {
    // Malformed or blocked storage reads as no picks.
  }
  return stored
}

function invalidate() {
  eventCache.clear()
  totalCache = null
  for (const fn of subscribers) fn()
}

function persist(next: Stored) {
  stored = next
  try {
    if (Object.keys(next.events).length === 0) localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch (err) {
    // The in-memory copy still updates, so the heart flips for this session.
    log('guest-picks', 'write failed', { error: String(err) })
  }
  invalidate()
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', e => {
    if (e.key !== null && e.key !== STORAGE_KEY) return
    stored = null
    invalidate()
  })
}

export function subscribeGuestPicks(fn: () => void): () => void {
  subscribers.add(fn)
  return () => subscribers.delete(fn)
}

export function getEventPicks(eventId: string): LocalPicks {
  const cached = eventCache.get(eventId)
  if (cached) return cached
  const entries = read().events[eventId]
  const picks: LocalPicks = entries ? new Map(Object.entries(entries)) : EMPTY_LOCAL_PICKS
  eventCache.set(eventId, picks)
  return picks
}

export function getTotalPickCount(): number {
  if (totalCache === null) {
    totalCache = Object.values(read().events).reduce((n, e) => n + Object.keys(e).length, 0)
  }
  return totalCache
}

export function getAllPicks(): Array<{ eventId: string; picks: LocalPicks }> {
  return Object.keys(read().events).map(eventId => ({ eventId, picks: getEventPicks(eventId) }))
}

export function nextStatus(current: InterestStatus | 'none'): InterestStatus | 'none' {
  return current === 'none' ? 'interested' : current === 'interested' ? 'going' : 'none'
}

export function writeGuestPick(eventId: string, setId: string, status: InterestStatus | null) {
  const events = { ...read().events }
  const picks = { ...events[eventId] }
  if (status) picks[setId] = status
  else delete picks[setId]
  if (Object.keys(picks).length === 0) delete events[eventId]
  else events[eventId] = picks
  persist({ v: 1, events })
}

// The account a watched sign-in claimed these picks for. Persisted so a reload
// mid-migration still finishes the job instead of stranding them on the device.
const OWNER_KEY = 'beacon_guest_set_picks_owner'

export function getPicksOwner(): string | null {
  try {
    return localStorage.getItem(OWNER_KEY)
  } catch {
    return null
  }
}

export function setPicksOwner(uid: string | null) {
  try {
    if (uid) localStorage.setItem(OWNER_KEY, uid)
    else localStorage.removeItem(OWNER_KEY)
  } catch {
    // Without it, a reload before migration finishes leaves the picks for the next watched sign-in.
  }
}

/** Removes a pick only if it still has `status` — another tab may have changed it mid-migration. */
export function removePickIfUnchanged(eventId: string, setId: string, status: InterestStatus) {
  if (read().events[eventId]?.[setId] !== status) return
  writeGuestPick(eventId, setId, null)
}
